import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { Button, Form } from "react-bootstrap";

import supabase, { authCallbackParams } from "../utils/Supabase";
import { showToast } from "../utils/Toasts";

import { LoadingText } from "../components/LoadingText";

import logo from "../img/sh-logo_site.png";

function NewPassword() {
  const [error, setError] = useState("");
  const [validated, setValidated] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const navigate = useNavigate();

  // Whether the user arrived here on a recovery link. Read from the snapshot taken
  // before the Supabase client started up, since it clears the hash out from under us.
  const hadRecoveryToken = useRef(
    authCallbackParams.hasAccessToken && authCallbackParams.type === "recovery"
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "newPassword") {
      setNewPassword(value);
    } else if (name === "repeatPassword") {
      setRepeatPassword(value);
    }

    // Reset validation when user starts typing
    if (validated) {
      setValidated(false);
    }
  };

  // Validation function
  const validateForm = () => {
    const isFormValid = (newPassword.trim() !== '') && (newPassword.length >= 10) && (newPassword === repeatPassword);
    return isFormValid;
  };

  // Validate session after checking for token
  useEffect(() => {
    const checkSession = async () => {
      // The link itself came back with an error, so there will never be a session
      if (authCallbackParams.error || authCallbackParams.errorCode) {
        // GoTrue sends the recovery email without checking bans but rejects the
        // link at /verify, so a banned user can request reset emails forever.
        // Send them to support instead of back to the form that re-sends.
        if (authCallbackParams.errorCode === "user_banned") {
          showToast("error", "Your account was restricted due to breaking the Terms of Service. If you feel this restriction was placed in error, please contact support.", 10000);
          navigate("/databaseLogin");
          return;
        }

        const expired = authCallbackParams.errorCode === "otp_expired";
        showToast(
          "error",
          expired
            ? "That password reset link has expired or was already used. Reset links are single use and some email providers open them automatically. Please request a new one."
            : `Password reset link error: ${authCallbackParams.errorDescription ?? authCallbackParams.error}`,
          10000
        );
        navigate("/resetPassword");
        return;
      }

      const { data: { session }, error } = await supabase.auth.getSession();

      if (error || !session) {
        showToast("error", "Invalid or expired password reset link. Please request a new one.");
        navigate("/resetPassword");
        return;
      }

      // If user has a session but didn't arrive with a recovery token, redirect them
      if (!hadRecoveryToken.current) {
        showToast("info", "Please request a password reset link to change your password.");
        navigate("/resetPassword");
        return;
      }

      setCheckingSession(false);
    };

    checkSession();
  }, [navigate]);

  // Warn user if they try to close the tab without submitting
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!passwordChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [passwordChanged]);

    useEffect(() => {
      if (newPassword.trim().length === 0 && repeatPassword.trim().length === 0) {
        setError("");
      } else if (newPassword.length < 10) {
        setError("Your password isn't long enough.");
      } else if (newPassword !== repeatPassword && repeatPassword.trim().length > 0) {
        setError("Your passwords don't match.");
      } else {
        setError("");
      }
    }, [newPassword, repeatPassword]);

  return checkingSession ? (null) : (
    <>
      <main className="d-flex" style={{ flex: "max-content" }}>
        <div className="m-auto">
          <img
            className="p-10 m-8 hivelogo"
            src={logo}
            alt="Script Hive logo"
            width={90}
          ></img>

          <div className="mt-3">
            <Form noValidate>
              <Form.Group>
                <Form.Label>Please create a new password at least 10 characters long.</Form.Label>
                <Form.Control
                  name="newPassword"
                  type="password"
                  className={"form-control mt-3" + (validated ? (newPassword.trim() === '' || newPassword.length < 10) ? ' is-invalid' : ' is-valid' : '')}
                  placeholder="Enter new password"
                  aria-label="Enter new password"
                  onChange={handleInputChange}
                  required
                />
                <Form.Control
                  name="repeatPassword"
                  type="password"
                  className={"form-control mt-3" + (validated ? (repeatPassword.trim() === '') ? ' is-invalid' : ' is-valid' : '')}
                  placeholder="Repeat new password"
                  aria-label="Repeat new password"
                  onChange={handleInputChange}
                  required
                />
                {error && (
                  <Form.Text className="invalid-feedback d-block mt-2">
                    {error}
                  </Form.Text>
                )}
                <Button
                  className="form-control mt-3"
                  type="submit"
                  disabled={isSubmitting}
                  onClick={async (e) => {
                    e.preventDefault();
                    const isFormValid = validateForm();
                    setValidated(true);

                    if (!isFormValid) {
                      return;
                    }

                    setIsSubmitting(true);

                    try {
                      const { data, error } = await supabase.auth.updateUser({
                        password: newPassword
                      });

                      if (error) throw error;

                      setPasswordChanged(true);
                      showToast("success", "Password updated successfully!");
                      navigate("/database");
                    } catch (error) {
                      console.error("Password update error:", error);
                      showToast("error", `Unable to update password: ${error.message}`);
                      setValidated(false);
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                >
                  {isSubmitting ? (
                      <LoadingText text="Submitting..." className="mb-0" />
                    ) : (
                      "Change password"
                    )
                  }
                </Button>
              </Form.Group>
            </Form>
          </div>
        </div>
      </main>
    </>
  );
}

export default NewPassword;